import {
  BillingBusinessLicense,
  BillingLicensePlans,
} from '@kizen/api/billing';
import { useMemo } from 'react';
import { arePlansEqual } from '../util';

type SelectedPlanResolutionArgs = {
  plans: BillingLicensePlans['license_templates'];
  currentPlan?: BillingBusinessLicense[number];
  currentSelectedPlanIndex: number | null;
};

export type SingleBusinessLicense = BillingBusinessLicense[number];
export type CurrentlyActivePlan = SingleBusinessLicense | undefined;
export type ResolvedPlanDetails =
  | {
      name: string;
      price?: SingleBusinessLicense['price'];
      limits: any;
      overage: any;
      ui_config: any;
      template_id?: string;
      plan_category?: string;
      config?: Record<string, unknown>;
    }
  | undefined;

const getUnifiedPlanDetails = (
  currentIndex: number | null,
  availableLicensePlans: BillingLicensePlans['license_templates'],
  activePlan: CurrentlyActivePlan
): ResolvedPlanDetails => {
  if (typeof currentIndex === 'number' && currentIndex >= 0) {
    const plan = availableLicensePlans[currentIndex];

    if (!plan) {
      return undefined;
    }

    return {
      name: plan.published_name,
      price: plan.price,
      limits: plan.config.limits,
      ui_config: plan.ui_config,
      overage: plan.config.overage,
      template_id: plan.id,
      plan_category: plan.plan_category,
      config: plan.config,
    };
  }

  return {
    name: activePlan?.license_plan.published_name ?? '',
    price: activePlan?.price,
    limits: activePlan?.config.limits,
    ui_config: activePlan?.ui_config,
    overage: activePlan?.config.overage,
    template_id: activePlan?.license_plan.id,
    plan_category: activePlan?.license_plan.plan_category,
    config: activePlan?.config,
  };
};

/*
    This hook takes in the currently selected plan index, as well as the current active license,
    and resolves it to a unified structure that can be used in a sequence, to determine
    the next tier up from a currently selected plan (not necessarily the active plan)
*/
export const useSelectedPlanResolution = (args: SelectedPlanResolutionArgs) => {
  const { plans, currentPlan, currentSelectedPlanIndex } = args;

  const computedActivePlanIndex = useMemo(() => {
    return plans.findIndex((plan) => {
      return arePlansEqual(plan, currentPlan);
    });
  }, [currentPlan, plans]);

  const unifiedSelectedPlanIndex =
    currentSelectedPlanIndex === null
      ? computedActivePlanIndex
      : currentSelectedPlanIndex;

  const currentSelectedPlan = useMemo(() => {
    return getUnifiedPlanDetails(currentSelectedPlanIndex, plans, currentPlan);
  }, [currentSelectedPlanIndex, currentPlan, plans]);

  //   Will be undefined if the next plan isn't self-serve (i.e enterprise, etc)
  const nextSelectedPlan = useMemo(() => {
    return getUnifiedPlanDetails(
      unifiedSelectedPlanIndex + 1,
      plans,
      currentPlan
    );
  }, [unifiedSelectedPlanIndex, currentPlan, plans]);

  const isLegacy = unifiedSelectedPlanIndex < 0;
  const planAmount = currentSelectedPlan?.price?.amount ?? 0;
  const isPaid = planAmount > 0;

  const showEstimator =
    (currentSelectedPlan?.price?.amount ?? 0) > 0 && !isLegacy;

  return {
    current: currentSelectedPlan,
    next: nextSelectedPlan,
    isLegacy,
    showEstimator,
    isPaid,
  };
};
